import { useState, useRef, useCallback } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Check, Loader2, RotateCcw, Activity } from 'lucide-react';
import { MUSICAL_KEYS, SONG_FEELS } from '@/lib/utils';
import { toast } from 'sonner';

export default function BpmTapTool({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [bpm, setBpm] = useState(null);
    const [taps, setTaps] = useState([]);
    const [animateTap, setAnimateTap] = useState(false);
    const [ripples, setRipples] = useState([]);
    const [key, setKey] = useState('');
    const [feel, setFeel] = useState('');
    const [saving, setSaving] = useState(false);
    
    const lastTapRef = useRef(null);
    const timeoutRef = useRef(null);
    const rippleIdRef = useRef(0);

    const handleTap = useCallback(() => {
        const now = Date.now();
        
        // Visual feedback - pulse animation
        setAnimateTap(true);
        setTimeout(() => setAnimateTap(false), 500);

        // Add ripple effect
        const rippleId = rippleIdRef.current++;
        setRipples(prev => [...prev, rippleId]);
        setTimeout(() => {
            setRipples(prev => prev.filter(id => id !== rippleId));
        }, 600);

        // Clear timeout for reset
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        
        // Reset if more than 2 seconds since last tap
        if (lastTapRef.current && now - lastTapRef.current > 2000) {
            setTaps([]);
            lastTapRef.current = now;
            return;
        }

        const newTaps = [...taps, now].slice(-8); // Keep last 8 taps
        setTaps(newTaps);
        lastTapRef.current = now;

        // Calculate BPM from intervals
        if (newTaps.length >= 2) {
            const intervals = [];
            for (let i = 1; i < newTaps.length; i++) {
                intervals.push(newTaps[i] - newTaps[i - 1]);
            }
            const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
            const calculatedBpm = Math.round(60000 / avgInterval);
            setBpm(Math.min(Math.max(calculatedBpm, 30), 300)); // Clamp between 30-300
        }

        // Reset after 3 seconds of inactivity
        timeoutRef.current = setTimeout(() => {
            setTaps([]);
        }, 3000);
    }, [taps]);

    const handleReset = () => {
        setBpm(null);
        setTaps([]);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const handleSave = async () => {
        if (!bpm && !key && !feel) {
            toast.error('Please add at least one piece of information');
            return;
        }

        setSaving(true);
        try {
            const fragment = await addFragment(songId, {
                type: 'metadata',
                label: 'Song Info',
                bpm: bpm || undefined,
                key: key || undefined,
                feel: feel || undefined
            });

            toast.success('Song info saved');
            onComplete(fragment?.id);
        } catch (error) {
            console.error('Error saving metadata:', error);
            toast.error('Failed to save info');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 max-w-sm mx-auto">
            {/* Tap area */}
            <div className="flex flex-col items-center gap-4">
                <div className="relative">
                    {/* Ripple effects */}
                    {ripples.map(id => (
                        <div key={id} className="tap-ripple" />
                    ))}
                    
                    {/* Main tap button */}
                    <button
                        onClick={handleTap}
                        className={`relative w-36 h-36 rounded-full flex flex-col items-center justify-center gap-1 bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-150 active:scale-95 ${
                            animateTap ? 'tap-pulse' : ''
                        }`}
                    >
                        <Activity className="w-10 h-10" />
                        <span className="text-base font-semibold">TAP</span>
                    </button>
                </div>

                <div className="text-center">
                    <div className="flex items-baseline justify-center gap-2">
                        <span className={`text-6xl font-display text-foreground tabular-nums transition-all ${
                            animateTap ? 'scale-110' : ''
                        }`}>
                            {bpm || '--'}
                        </span>
                        <span className="text-xl text-muted-foreground">BPM</span>
                    </div>
                    
                    {/* Tap count indicator */}
                    <div className="flex items-center justify-center gap-1 mt-3">
                        {[...Array(8)].map((_, i) => (
                            <div
                                key={i}
                                className={`w-2 h-2 rounded-full transition-all duration-150 ${
                                    i < taps.length 
                                        ? 'bg-primary scale-100' 
                                        : 'bg-muted scale-75'
                                }`}
                            />
                        ))}
                    </div>
                </div>

                {taps.length > 0 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleReset}
                        className="text-muted-foreground hover:scale-105 transition-transform"
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset
                    </Button>
                )}

                <p className="text-sm text-muted-foreground text-center">
                    {taps.length === 0 
                        ? 'Tap along with the beat to calculate tempo'
                        : taps.length < 4 
                            ? `Keep tapping (${4 - taps.length} more for accuracy)`
                            : 'Tempo detected! Keep tapping to refine'
                    }
                </p>
            </div>

            {/* Additional metadata */}
            <div className="space-y-4 pt-4 border-t border-border">
                <div className="space-y-2">
                    <Label>Key</Label>
                    <Select value={key} onValueChange={setKey}>
                        <SelectTrigger className="hover:border-primary/50 transition-colors">
                            <SelectValue placeholder="Select key (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                            {MUSICAL_KEYS.map((k) => (
                                <SelectItem key={k} value={k}>{k}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label>Feel</Label>
                    <Select value={feel} onValueChange={setFeel}>
                        <SelectTrigger className="hover:border-primary/50 transition-colors">
                            <SelectValue placeholder="Select feel (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                            {SONG_FEELS.map((f) => (
                                <SelectItem key={f} value={f}>{f}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label>Or enter BPM manually</Label>
                    <Input
                        type="number"
                        min="30"
                        max="300"
                        value={bpm || ''}
                        onChange={(e) => setBpm(e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="e.g., 120"
                        className="hover:border-primary/50 transition-colors"
                    />
                </div>
            </div>

            <Button
                onClick={handleSave}
                disabled={saving || (!bpm && !key && !feel)}
                className="w-full h-12 hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save Info
            </Button>
        </div>
    );
}